import type {
  SalesOrderOutcomeReason as SalesOrderOutcomeReasonValue,
  SalesOrderOutcomeReasonInput,
} from '@/lib/services/sales-orders/sales-orders.types'

/** A reason staff can choose for a new cancellation or refund. */
interface ActiveOutcomeReason {
  value: SalesOrderOutcomeReasonInput
  label: string
  isActive?: true
}

/** A reason older records may carry; the API no longer accepts it. */
interface LegacyOutcomeReason {
  value: Exclude<SalesOrderOutcomeReasonValue, SalesOrderOutcomeReasonInput>
  label: string
  isActive: false
}

type OutcomeReason = ActiveOutcomeReason | LegacyOutcomeReason

export interface SalesOrderOutcomeReasonOption {
  id: SalesOrderOutcomeReasonInput
  name: string
}

export const SalesOrderOutcomeReason = {
  CustomerRequest: { value: 'customer_request', label: 'Customer request' },
  ProductIssue: { value: 'product_issue', label: 'Product issue' },
  FulfillmentIssue: { value: 'fulfillment_issue', label: 'Fulfillment issue' },
  DeliveryIssue: { value: 'delivery_issue', label: 'Delivery issue' },
  OrderOrPaymentIssue: { value: 'order_or_payment_issue', label: 'Order or payment issue' },
  InternalMistake: { value: 'internal_mistake', label: 'Internal mistake' },
  Other: { value: 'other', label: 'Other' },

  // Legacy values kept for historical display only.
  DuplicateOrder: { value: 'duplicate_order', label: 'Duplicate order', isActive: false },
  ProductUnavailable: {
    value: 'product_unavailable',
    label: 'Product unavailable',
    isActive: false,
  },
  FulfillmentTimingIssue: {
    value: 'fulfillment_timing_issue',
    label: 'Fulfillment timing issue',
    isActive: false,
  },
  DeliveryOrAddressIssue: {
    value: 'delivery_or_address_issue',
    label: 'Delivery or address issue',
    isActive: false,
  },
  PaymentIssue: { value: 'payment_issue', label: 'Payment issue', isActive: false },
  ShopEntryMistake: { value: 'shop_entry_mistake', label: 'Shop entry mistake', isActive: false },
  CustomerDissatisfied: {
    value: 'customer_dissatisfied',
    label: 'Customer dissatisfied',
    isActive: false,
  },
  SoftwareIssue: { value: 'software_issue', label: 'Software issue', isActive: false },
  TestOrSpam: { value: 'test_or_spam', label: 'Test or spam', isActive: false },
} satisfies Record<string, OutcomeReason>

export const salesOrderOutcomeReasonOptions: SalesOrderOutcomeReasonOption[] =
  Object.values<OutcomeReason>(SalesOrderOutcomeReason)
    .filter((reason) => reason.isActive !== false)
    .map((reason) => ({
      id: reason.value,
      name: reason.label,
    }))

export function getSalesOrderOutcomeReasonLabel(value: string | null | undefined): string | null {
  return (
    Object.values(SalesOrderOutcomeReason).find((reason) => reason.value === value)?.label ?? null
  )
}

export function outcomeReasonRequiresNote(value: string | null | undefined): boolean {
  return value === SalesOrderOutcomeReason.Other.value
}
